import type { ChatChannelUserItem } from '@module/apis/client-side/chat/type';
import type { PlayInfo } from '@module/apis/client-side/live/type';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { deviceIconMap } from '@module/map/device';
import { useFetchChatChannelUserLisQuery } from '@module/queries/chat/chatQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import useSocketStore from '@module/stores/useSocketStore';
import { GenderFemale, GenderMale } from '@module/svg/icons';
import type { TabList } from '@module/types/broadcast-setting/type';
import type { FanLevel } from '@module/types/centrifuge/publicMessageTypes';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import TimeConstants from '@module/utils/item';
import masking from '@module/utils/masking';
import GraySearchInput from '@pc/components/atoms/input/GraySearchInput';
import NotFoundDisplay from '@pc/components/result/NotFoundDisplay';
import React, { type Dispatch, type FormEvent, type SetStateAction, useState } from 'react';
import ChatGradePrefixImage from '../_components/ChatGradePrefixImage';
import ViewerInteractionMenu from '../_components/ViewerInteractionMenu';
import VipFanListBox from '../_components/VipFanListBox';

function getUserType(user: ChatChannelUserItem) {
  if (user.isRoomMaster === 'Y') {
    return 'bj';
  }
  if (user.isManager === 'Y') {
    return 'manager';
  }

  if (user.isGuest) {
    return 'guest';
  }

  return 'chatter';
}

interface Props {
  isRoomMaster: boolean;
  isManager: boolean;
  playInfo: PlayInfo;
  endBroadcast?: boolean;
  isChattingMode?: boolean;
  setStepType: Dispatch<SetStateAction<TabList>>;
}

function ViewerListContainer({
  isManager,
  playInfo,
  isRoomMaster,
  endBroadcast = false,
  isChattingMode,
  setStepType,
}: Props) {
  const { token, channel } = useSocketStore();

  const [searchQuery, setSearchQuery] = useState('');

  const [menuVisibleOffset, setMenuVisibleOffset] = useState(0);

  const pushModal = useModalStore((state) => state.pushModal);

  const { userInfo } = useGetLoginInfoQuery();

  const { data: userList } = useFetchChatChannelUserLisQuery(
    { token: token || '', channel: channel || '', locale: 'all' },
    {
      refetchInterval: TimeConstants.ONE_SECOND * 5,
      enabled: (isRoomMaster || isManager) && !!token && !!channel && !endBroadcast,
    },
  );

  const handleSearchFilterSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const input = form.querySelector('input[name="searchInput"]') as HTMLInputElement;

    if (!input) return;
    if (input.value?.length === 1) {
      input.blur();
      pushModal({
        type: MODAL_TYPE_PC.CONFIRM_MODAL,
        id: 'ViewerListContainer',
        options: { description: '2글자 이상 입력해 주세요.' },
      });
      return;
    }
    setSearchQuery(input.value);
  };

  const filteredUserList =
    searchQuery.length >= 2 && userList
      ? userList.list.filter((item) => {
          if (item.isGuest) return false;
          return item.nick.includes(searchQuery) || item.id.includes(searchQuery);
        })
      : userList?.list || [];

  // 100개 이하만 보여줌
  const limitedUserList = filteredUserList.slice(0, 100);

  return (
    <div className='h-full border-l'>
      {!isChattingMode && <VipFanListBox playInfo={playInfo} setStepType={setStepType} />}
      <div className='mb-2 border-borderBg01 border-b-[1px] p-[10px]'>
        <GraySearchInput
          className='h-10'
          onSearch={handleSearchFilterSubmit}
          placeholder='닉네임 또는 아이디 검색'
          name='searchInput'
        />
      </div>

      <div className='w-full px-2'>
        {limitedUserList.length === 0 && <NotFoundDisplay text='검색결과가 없습니다.' />}
        {limitedUserList.length > 0 &&
          limitedUserList.map((user) => {
            return (
              <React.Fragment key={user.id}>
                <div className='relative'>
                  <button
                    className='flex w-full items-center justify-between py-2'
                    onClick={() => {
                      if (user.idx === userInfo?.idx) return;
                      setMenuVisibleOffset(user.idx);
                    }}
                    disabled={user.isRoomMaster === 'Y'}
                  >
                    <div className='flex items-center'>
                      <div className='mr-2'>
                        <ChatGradePrefixImage
                          type={getUserType(user)}
                          ranking={user.fan?.rank}
                          level={user.fan?.level as FanLevel}
                          className=' h-[14px] w-[14px]'
                        />
                      </div>
                      <div className='text-[14px] text-baseFg'>
                        {user.nick}({user.isRoomMaster === 'Y' ? user.id : masking(user.id)})
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      {user.sex === 'M' && <GenderMale />}
                      {user.sex === 'F' && <GenderFemale />}
                      {user.deviceType && deviceIconMap[user.deviceType]}
                    </div>
                  </button>
                  <ViewerInteractionMenu
                    playInfo={playInfo}
                    isVisible={!!menuVisibleOffset && user.idx === menuVisibleOffset}
                    user={{ isManager, isRoomMaster }}
                    viewer={{
                      idx: user.idx,
                      id: user.id,
                      nick: user.nick,
                      isJapanese: user.lcl === 'jp',
                      isManager: user.isManager === 'Y',
                      isRoomMaster: user.isRoomMaster === 'Y',
                    }}
                    onClose={() => setMenuVisibleOffset(0)}
                  />
                </div>
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
}

export default ViewerListContainer;
